import { useState } from 'react';
import { AsyncBoundary, Notice } from '@kollez/design-system';
import { usePlatformTxns, type CatalogTxnStatus } from '@/lib/queries';
import { formatINR } from '@kollez/shared';

/**
 * /platform/catalog-transactions (Page 68b) — Cross-tenant Pool-2 revenue ledger.
 *
 * GET /platform/transactions returns rows + rollup by status with the
 * Kollez / partner split. Platform ops needs to see this to reconcile
 * revenue with partners, watch for failed-payment spikes per partner,
 * and cross-check against tenant billing.
 */

const STATUS_LABEL: Record<CatalogTxnStatus, string> = {
  pending: 'Pending',
  succeeded: 'Succeeded',
  failed: 'Failed',
  refunded: 'Refunded',
};

function statusTone(s: CatalogTxnStatus): string {
  if (s === 'succeeded') return 'badge-success';
  if (s === 'refunded') return 'badge-info';
  if (s === 'failed') return 'badge-danger';
  return 'badge-warning';
}

function fmtDay(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

export function PlatformCatalogTransactionsPage() {
  const [status, setStatus] = useState<CatalogTxnStatus | ''>('');
  const [partnerId, setPartnerId] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const list = usePlatformTxns({ status, partnerId, tenantId, from, to, limit: 100 });
  const rows = list.data?.rows ?? [];
  const rollup = list.data?.rollup ?? {};

  const totals = Object.values(rollup).reduce(
    (acc, r) => ({
      amountInr: acc.amountInr + (r.amountInr ?? 0),
      kollezInr: acc.kollezInr + (r.kollezInr ?? 0),
      partnerInr: acc.partnerInr + (r.partnerInr ?? 0),
      count: acc.count + (r.count ?? 0),
    }),
    { amountInr: 0, kollezInr: 0, partnerInr: 0, count: 0 },
  );
  const succeeded = rollup.succeeded;

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Platform · Revenue</p>
          <h1 className="h-page">Catalog transactions</h1>
          <p className="subhead">
            Every Pool-2 catalog charge across all tenants. Reconcile with partners here.
            Filter by status, partner, or tenant to isolate a chunk before exporting to accounting.
          </p>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <div className="card kpi">
          <span className="kpi-label">Total charged</span>
          <span className="kpi-value">{formatINR(totals.amountInr)}</span>
          <span className="kpi-meta">{totals.count} transaction{totals.count === 1 ? '' : 's'}</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Kollez share</span>
          <span className="kpi-value">{formatINR(totals.kollezInr)}</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Partner share</span>
          <span className="kpi-value">{formatINR(totals.partnerInr)}</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Succeeded</span>
          <span className="kpi-value">{succeeded?.count ?? 0}</span>
          <span className="kpi-meta">{formatINR(succeeded?.amountInr ?? 0)}</span>
        </div>
      </div>

      <div className="card">
        <div className="filterbar">
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as CatalogTxnStatus | '')}>
            <option value="">Any status</option>
            {(Object.entries(STATUS_LABEL) as Array<[CatalogTxnStatus, string]>).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <input className="input" placeholder="Partner ID (ObjectId)" value={partnerId} onChange={(e) => setPartnerId(e.target.value.trim())} />
          <input className="input" placeholder="Tenant ID (ObjectId)" value={tenantId} onChange={(e) => setTenantId(e.target.value.trim())} />
          <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} title="From" />
          <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} title="To" />
          <span className="fb-count">{list.data?.meta?.total ?? rows.length}</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={list.isLoading}
          error={list.error}
          empty={!list.isLoading && rows.length === 0}
          emptyTitle="No transactions in scope"
          emptyHint="Loosen your filters or widen the date range."
          onRetry={() => void list.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Partner</th>
                  <th className="hide-mobile">Tenant</th>
                  <th>Stage</th>
                  <th className="r">Amount</th>
                  <th className="r hide-mobile">Kollez</th>
                  <th className="r hide-mobile">Partner</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t._id}>
                    <td className="cell-sub">{fmtDay(t.initiatedAt)}</td>
                    <td>
                      <div className="cell-title">
                        {typeof t.partner === 'object' && t.partner?.name ? t.partner.name : '—'}
                      </div>
                      {typeof t.opportunity === 'object' && t.opportunity?.title && (
                        <div className="cell-sub">{t.opportunity.title}</div>
                      )}
                    </td>
                    <td className="hide-mobile cell-sub">
                      {typeof t.studentPrimaryTenant === 'object' && t.studentPrimaryTenant?.name
                        ? t.studentPrimaryTenant.name
                        : '—'}
                    </td>
                    <td className="cell-sub">{t.stageKey ?? '—'}</td>
                    <td className="r">{formatINR(t.amountInr)}</td>
                    <td className="r hide-mobile cell-sub">{formatINR(t.split?.kollezInr ?? 0)}</td>
                    <td className="r hide-mobile cell-sub">{formatINR(t.split?.partnerInr ?? 0)}</td>
                    <td><span className={`badge ${statusTone(t.status)}`}>{STATUS_LABEL[t.status]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      <Notice tone="info">
        <b>Reconciliation.</b> Amounts here are what students paid. Partner-side settlement runs
        on a separate cadence — cross-check against the partner's monthly statement before
        confirming a payout.
      </Notice>
    </div>
  );
}
